import { Component, inject, signal, OnInit, OnDestroy } from '@angular/core';
import { RouterOutlet, Router, NavigationEnd, ActivatedRoute } from '@angular/router';

import { filter, Subscription } from 'rxjs';
import { MIXPANEL_EVENTS, pageNameFromUrl, toMixpanelId } from './core/analytics/mixpanel-events';
import { MixPanelService } from './core/analytics/mixpanel.service';
import { HeaderComponent } from './shared/components/header/header.component';
import { SidebarComponent } from './shared/components/sidebar/sidebar.component';
import { ToastComponent } from './shared/components/toast/toast.component';
import { ModalComponent } from './shared/components/modal/modal.component';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, HeaderComponent, SidebarComponent, ToastComponent, ModalComponent],
  template: `
    <div class="app-layout">
      @if (showHeader()) {
        <app-header />
      }
      <div class="app-content">
        @if (showSidebar()) {
          <app-sidebar />
        }
        <main class="main-content">
          <router-outlet />
        </main>
      </div>
    </div>
    <app-toast />
    <app-modal />
  `,
  styles: [`
    .app-layout {
      display: flex;
      flex-direction: column;
      height: 100vh;
      width: 100%;
      overflow: hidden;
    }
    
    .app-content {
      display: flex;
      flex: 1;
      overflow: hidden;
      min-width: 0;
    }
    
    .main-content {
      display: flex;
      flex-direction: column;
      flex: 1;
      overflow: hidden;
      min-width: 0;
      min-height: 0;
      background-color: var(--bg-main);
    }
  `]
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly mixPanelService = inject(MixPanelService);
  private routerSub?: Subscription;
  /** Last URL sent as a page view, so the same navigation is never tracked twice. */
  private lastTrackedPageUrl: string | null = null;

  showHeader = signal(true);
  showSidebar = signal(false);

  private resolveShowHeaderFromRoute(): boolean {
    let route = this.activatedRoute.firstChild;
    while (route?.firstChild) {
      route = route.firstChild;
    }
    // During hard reload there is a short period where the final route is not resolved yet.
    // Return a safe value to avoid flashing the app shell on routes like /unauthorized.
    if (!route?.snapshot?.routeConfig) {
      return false;
    }
    const routeValue = route?.snapshot?.data?.['showHeader'];
    if (typeof routeValue === 'boolean') {
      return routeValue;
    }
    return true;
  }

  private resolveShowSidebarFromRoute(): boolean {
    let route = this.activatedRoute.firstChild;
    while (route?.firstChild) {
      route = route.firstChild;
    }
    // Keep sidebar hidden until route is fully available to prevent first-paint flicker.
    if (!route?.snapshot?.routeConfig) {
      return false;
    }
    const routeValue = route?.snapshot?.data?.['showSidebar'];
    if (typeof routeValue === 'boolean') {
      return routeValue;
    }
    return true;
  }

  ngOnInit(): void {
    this.showHeader.set(this.resolveShowHeaderFromRoute());
    this.showSidebar.set(this.resolveShowSidebarFromRoute());

    // Subscribe to route changes
    this.routerSub = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
    ).subscribe((event) => {
      this.showHeader.set(this.resolveShowHeaderFromRoute());
      this.showSidebar.set(this.resolveShowSidebarFromRoute());
      this.trackPageView(event.urlAfterRedirects);
    });
  }

  /** DD App - page view: once per NavigationEnd; routes outside the catalog (login) are skipped. */
  private trackPageView(url: string): void {
    // Record every URL (catalog or not) so a return to a previously tracked page is not treated as a repeat.
    const isRepeat = url === this.lastTrackedPageUrl;
    this.lastTrackedPageUrl = url;
    const pageName = pageNameFromUrl(url);
    if (!pageName || isRepeat) return;

    let route = this.activatedRoute.snapshot;
    while (route.firstChild) route = route.firstChild;
    const dictionaryId = pageName === 'dictionary detail' ? toMixpanelId(route.paramMap.get('id')) : undefined;
    const categoryId = pageName === 'data dictionary' ? toMixpanelId(route.queryParamMap.get('category')) : undefined;

    this.mixPanelService.track(MIXPANEL_EVENTS.APP_PAGE_VIEW, {
      pageName,
      ...(dictionaryId !== undefined && { dictionaryId }),
      ...(categoryId !== undefined && { categoryId }),
    });
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
  }
}
